import dayjs, { type Dayjs } from 'dayjs';
import { isArray, isObject } from 'lodash-es';
import type { JsonValue } from 'type-fest';

// Dates are converted to strings
export type SerializedJson<T> = T extends Dayjs | Date
  ? string
  : T extends object
    ? {
        [K in keyof T]: SerializedJson<T[K]>;
      }
    : T;

function convertIsoStringToDayjs(text: string): string | Dayjs {
  if (text.length === 24 && text.endsWith('Z')) {
    const date = new Date(text);
    if (date.toISOString() === text) {
      return dayjs(date);
    }
  }

  return text;
}

function convertIsoStringToDate(text: string): string | Date {
  if (text.length === 24 && text.endsWith('Z')) {
    const date = new Date(text);
    if (date.toISOString() === text) {
      return date;
    }
  }

  return text;
}

function isJsonObject(obj: unknown): obj is Record<string, JsonValue> {
  return isObject(obj) && !isArray(obj);
}

// WARN: This mutates the object in place
// We map all ISO strings to DayJS (or native Date) objects
export function automapDates<T>(
  data: SerializedJson<T>,
  preferNativeDate = false,
): T {
  if (!data) {
    return data as T;
  }

  if (typeof data === 'string') {
    return (
      preferNativeDate
        ? convertIsoStringToDate(data)
        : convertIsoStringToDayjs(data)
    ) as T;
  }

  if (isArray(data)) {
    for (let i = 0; i < data.length; i++) {
      data[i] = automapDates(data[i], preferNativeDate);
    }

    return data as T;
  }

  if (isJsonObject(data)) {
    for (const key in data) {
      if (Object.hasOwn(data, key)) {
        // We map the type
        // @ts-expect-error
        data[key] = automapDates(data[key], preferNativeDate);
      }
    }
  }

  return data as T;
}
